import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseOutlined, FilterOutlined } from '@ant-design/icons';
import { Typography } from 'antd';

import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { Popover } from '@/shared/ui/popover';

import { countActiveFilters } from '../../lib/active-filters-count';
import { usePatternFilterValues } from '../../model/selectors/pattern-filter';
import { clearFilter } from '../../model/slices/pattern-filter.slice';
import { PatternFilterForm } from '../pattern-filter-form/pattern-filter-form';

import cls from './pattern-filter-button.module.css';

export const PatternFilterButton = memo(() => {
    const { t } = useTranslation('pattern');
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const filterValues = usePatternFilterValues();
    const [resetKey, setResetKey] = useState(0);

    const activeFiltersCount = countActiveFilters(filterValues.filters);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
    };

    const handleResetFilters = useCallback(() => {
        dispatch(clearFilter());
        // Increment resetKey to force PatternFilterForm to reset
        setResetKey((prev) => prev + 1);
    }, [dispatch]);

    const filterContent = (
        <div className={cls.filterContent}>
            <div className={cls.filterTitleContainer}>
                <Typography.Title
                    className={cls.filterTitle}
                    level={5}
                >
                    {t('filter')}
                </Typography.Title>
            </div>

            <PatternFilterForm
                afterSubmit={() => setIsOpen(false)}
                resetKey={resetKey}
            />
        </div>
    );

    return (
        <div className={cls.root}>
            <div className={cls.container}>
                {activeFiltersCount > 0 && (
                    <div className={cls.filtersInfo}>
                        <span className={cls.filterCount}>
                            {t('filters.active', { count: activeFiltersCount })}
                        </span>
                        <Button
                            className={cls.resetButton}
                            icon={<CloseOutlined />}
                            onClick={handleResetFilters}
                            variant="ghost"
                        />
                    </div>
                )}
                <Popover
                    className={cls.popover}
                    content={filterContent}
                    onOpenChange={handleOpenChange}
                    open={isOpen}
                    overlayClassName={cls.overlay}
                    placement="bottomRight"
                    trigger={
                        <Button
                            className={isOpen ? cls.activeFilterButton : ''}
                            data-tour="pattern-studio-filters"
                            icon={<FilterOutlined />}
                            id="PatternFilterButton"
                            title={t('filter')}
                        />
                    }
                />
            </div>
        </div>
    );
});

PatternFilterButton.displayName = 'PatternFilterButton';
